import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { ArrowLeft, Calendar, CheckCircle, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../common/components/Button";
import PageHeader from "../../../common/components/PageHeader";
import { toast } from "../../../common/components/Toast";
import { SPACING } from "../../../common/constants/spacing";
import { useSupportTicket } from "../../../support/api/support";

function SupportTicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { ticket, loading, updateTicket, updating } =
    useSupportTicket(ticketId);
  const [localStatus, setLocalStatus] = useState(ticket?.status || "");

  useEffect(() => {
    if (ticket?.status) {
      setLocalStatus(ticket.status);
    }
  }, [ticket?.status]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateTicket({ status: newStatus });
      toast.success("Ticket status updated successfully!");
    } catch (error) {
      toast.error("Failed to update ticket status");
      console.error("Error updating status:", error);
      setLocalStatus(ticket.status);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "success",
      medium: "warning",
      high: "error",
      urgent: "error",
    };
    return colors[priority] || "default";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Ticket not found
        </Typography>
        <Button
          onClick={() => navigate("/admin/support")}
          sx={{ mt: SPACING.sm }}
        >
          Back to Tickets
        </Button>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title={ticket.subject}
        description={`Ticket #${ticket.ticket_number}`}
        appendActions={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate("/admin/support")}
          >
            Back to List
          </Button>
        }
      />

      <Grid container spacing={3}>
        {/* Left Column - Messages */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: 3 }}>
              Message
            </Typography>

            <Typography
              sx={{
                whiteSpace: "pre-wrap",
              }}
            >
              {ticket.content}
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Mail size={18} />}
                onClick={() => {
                  const subject = encodeURIComponent(`Re: ${ticket.subject}`);
                  const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${ticket.customer?.email}&su=${subject}`;
                  window.open(gmailUrl, "_blank");
                }}
              >
                Reply via Email
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Right Column - Ticket Details */}
        <Grid size={{ xs: 12, lg: 5 }}>
          {/* Status */}
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h3" sx={{ mb: SPACING.sm, fontWeight: 600 }}>
              Status
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={localStatus}
                onChange={(e) => {
                  setLocalStatus(e.target.value);
                  handleStatusChange(e.target.value);
                }}
                disabled={updating}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Card>

          {/* Ticket Info */}
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h3" sx={{ mb: SPACING.sm, fontWeight: 600 }}>
              Ticket Information
            </Typography>

            <Box sx={{ mb: SPACING.sm }}>
              <Typography variant="caption" color="text.secondary">
                Priority
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={
                    ticket?.priority?.charAt(0).toUpperCase() +
                    ticket?.priority?.slice(1)
                  }
                  size="small"
                  color={getPriorityColor(ticket.priority)}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: SPACING.sm }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <Calendar size={14} />
                Created
              </Typography>
              <Typography variant="bodySm">
                {formatDate(ticket.created_at)}
              </Typography>
            </Box>

            {ticket.resolved_at && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <CheckCircle size={14} />
                  Resolved
                </Typography>
                <Typography variant="bodySm">
                  {formatDate(ticket.resolved_at)}
                </Typography>
              </Box>
            )}
          </Card>

          {/* Customer Information */}
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ mb: SPACING.sm, fontWeight: 600 }}>
              Customer Information
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: SPACING.sm }}>
              <Avatar
                src={ticket.customer?.avatar}
                sx={{ width: 56, height: 56 }}
              >
                {ticket.customer?.name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {ticket.customer?.name}
                </Typography>
                <Typography variant="bodySm" color="text.secondary">
                  {ticket.customer?.email}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: SPACING.xs }}>
              <Typography variant="caption" color="text.secondary">
                User ID
              </Typography>
              <Typography variant="bodySm">{ticket.customer?.id}</Typography>
            </Box>

            {ticket.customer?.is_staff !== undefined && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Role
                </Typography>
                <Typography variant="bodySm">
                  {ticket.customer?.is_staff ? "Staff" : "Customer"}
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

export default SupportTicketDetail;
